import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Play } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useRecordingLabel } from '@/hooks/useRecordingLabel';
import { ApiError } from '@/lib/api';
import { audioAt, clock } from '@/lib/audio';
import { fetchCadeAudioRecording } from '@/services/cade';
import type { CadeRecordingVotes, CadeSpokenEvent, CadeSpokenName } from '@/services/types';

/**
 * The votes SAID aloud in one recording: the proclaimed results, the vote calls and the
 * recusals, each with the exact words, the second they start at, and a button that plays the
 * audio from there.
 *
 * Read the heading as "who was called and what they answered", not as the roll call. A vote
 * that is never called aloud is absent, an answer that did not read cleanly is left out, and
 * the person on a vote call is the one the president NAMED -- Whisper identifies no speaker.
 */
export function CadeAudioRecordingPage() {
  const { t } = useTranslation(['cade']);
  const { key = '' } = useParams();
  const label = useRecordingLabel();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'audio', 'recording', key],
    queryFn: () => fetchCadeAudioRecording(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const play = (seconds: number) => {
    const element = audioRef.current;
    if (!element) return;
    element.currentTime = seconds;
    void element.play().catch(() => undefined);
  };

  if (isPending) {
    return (
      <div className="p-6">
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('cade:audio.detail.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <BackLink />
      <Answer answer={data.votes} help={t('cade:audio.detail.help')}>
        {(votes: CadeRecordingVotes) => (
          <>
            <header className="space-y-1">
              <h1 className="text-lg font-semibold text-foreground">{label(votes.recording)}</h1>
              <p className="text-sm text-muted-foreground">{t('cade:audio.detail.subtitle')}</p>
            </header>
            <p
              role="note"
              className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground"
              data-machine-transcript
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden
              />
              {t('cade:audio.detail.notice')}
            </p>
            {votes.recording.audio_url ? (
              // `preload="none"`: a session is hours of audio and most visits never press play.
              // No caption track: CADE publishes none, and this page IS the text alternative --
              // every passage below is the recording's words with the second they start at.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio
                ref={audioRef}
                controls
                preload="none"
                src={votes.recording.audio_url}
                className="w-full"
              />
            ) : null}
            <div className="flex flex-wrap gap-4 text-sm">
              <Link
                to={`/cade/audio/${votes.recording.key}/transcript`}
                className="text-primary underline-offset-4 hover:underline"
                data-full-transcript
              >
                {t('cade:audio.detail.fullTranscript')}
              </Link>
              <Link
                to={`/cade/audio/topics?topic=market&recording=${votes.recording.key}`}
                className="text-primary underline-offset-4 hover:underline"
                data-recording-topics
              >
                {t('cade:audio.detail.topics')}
              </Link>
            </div>
            <EventSection
              title={t('cade:audio.detail.results')}
              events={votes.results}
              votes={votes}
              play={play}
              kind="result"
            />
            <EventSection
              title={t('cade:audio.detail.calls')}
              events={votes.vote_calls}
              votes={votes}
              play={play}
              kind="vote_call"
            />
            <EventSection
              title={t('cade:audio.detail.recusals')}
              events={votes.recusals}
              votes={votes}
              play={play}
              kind="recusal"
            />
          </>
        )}
      </Answer>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['cade']);
  return (
    <Link
      to="/cade/audio"
      className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('cade:audio.detail.back')}
    </Link>
  );
}

function name(value: CadeSpokenName | null): string {
  return value ? (value.canonical ?? value.spoken) : '';
}

function EventSection({
  title,
  events,
  votes,
  play,
  kind,
}: {
  title: string;
  events: CadeSpokenEvent[];
  votes: CadeRecordingVotes;
  play: (seconds: number) => void;
  kind: 'result' | 'vote_call' | 'recusal';
}) {
  const { t } = useTranslation(['cade']);
  return (
    <section className="space-y-2" data-audio-events={kind}>
      <h2 className="text-sm font-semibold text-foreground">
        {title} <span className="font-normal text-muted-foreground">({events.length})</span>
      </h2>
      {events.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('cade:audio.detail.none')}</p>
      ) : (
        <ul className="space-y-2">
          {events.map((event) => (
            <li
              key={`${event.event_type}-${event.start_s}`}
              className="space-y-1 rounded-md border p-3"
              data-audio-event
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => play(event.start_s)}
                  className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-foreground"
                >
                  <Play className="h-3 w-3" aria-hidden />
                  <span className="tabular-nums">{clock(event.start_s)}</span>
                </button>
                <EventBadges event={event} />
                {audioAt(votes.recording.audio_url, event.start_s) ? (
                  <a
                    href={audioAt(votes.recording.audio_url, event.start_s) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {t('cade:audio.detail.openAudio')}
                  </a>
                ) : null}
                <Link
                  to={`/cade/audio/${votes.recording.key}/transcript#seg-${event.seg_from}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t('cade:audio.detail.seeInTranscript')}
                </Link>
              </div>
              <blockquote className="text-sm text-foreground">“{event.quote}”</blockquote>
              {event.event_type === 'result' && event.complete === false ? (
                <p className="text-xs text-muted-foreground">{t('cade:audio.detail.incomplete')}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EventBadges({ event }: { event: CadeSpokenEvent }) {
  const { t } = useTranslation(['cade']);
  if (event.event_type === 'result') {
    return (
      <>
        <span className="rounded bg-muted px-1.5 py-0.5 text-foreground">
          {t(`cade:audio.detail.outcome.${event.outcome_kind ?? 'unknown'}`, {
            defaultValue: event.outcome_kind ?? '',
          })}
        </span>
        {event.dissenters.length > 0 ? (
          <span>
            {t('cade:audio.detail.outvoted', {
              names: event.dissenters.map((d) => name(d)).join(', '),
            })}
          </span>
        ) : null}
      </>
    );
  }
  if (event.event_type === 'vote_call') {
    return (
      <>
        <span className="font-medium text-foreground">
          {t('cade:audio.detail.called', { name: name(event.voter) })}
        </span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-foreground">
          {t(`cade:audio.detail.position.${event.position ?? 'unknown'}`, {
            defaultValue: event.position ?? '',
            target: name(event.target),
          })}
        </span>
      </>
    );
  }
  return <span className="font-medium text-foreground">{name(event.voter)}</span>;
}
